import { useEffect, useRef, useState } from "react";
import { endpoints } from "../../lib/api";
import { formatCurrency } from "../../lib/romEngine";
import { exportNodeToPDF } from "../../lib/pdf";
import { APP_NAME, FRANCHISE_NAME } from "../../lib/branding";
import { X, FilePdf, CircleNotch, CheckCircle, Sparkle } from "@phosphor-icons/react";

const LOSS_LABEL = { water: "Water Damage", fire: "Fire & Smoke", mould: "Mould Remediation", storm: "Storm Damage" };

function buildSummary(result) {
    return {
        grand_total: result.grand_total,
        labour_total: result.labour_total,
        consumables_total: result.consumables_total,
        equipment_total: result.equipment_total,
        subs_total: result.subcontractor_total ?? result.subs_total ?? 0,
        incidentals_total: result.incidentals_total,
        confidence_pct: result.confidence_pct,
        days: result.days,
        crew_size: result.crew_size,
    };
}

export function ProposalModal({ open, onClose, result, inputs, refLabel }) {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [downloading, setDownloading] = useState(false);
    const docRef = useRef(null);

    useEffect(() => {
        if (!open || !result) return;
        setData(null); setErr(""); setLoading(true);
        (async () => {
            try {
                const summary = buildSummary(result);
                const { data: d } = await endpoints.aiProposal({ inputs, summary });
                setData(d);
            } catch (e) {
                setErr("Couldn't generate the proposal narrative. Please try again.");
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    if (!open) return null;

    const s = result ? buildSummary(result) : {};
    const today = new Date().toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
    const lossLabel = LOSS_LABEL[inputs?.lossType] || "Restoration";

    const buckets = [
        ["Labour & Supervision", s.labour_total],
        ["Consumables & PPE", s.consumables_total],
        ["Equipment", s.equipment_total],
        ["Subcontractors", s.subs_total],
        ["Mobilisation & Incidentals", s.incidentals_total],
    ].filter(([, v]) => Number(v) > 0);

    async function download() {
        if (!docRef.current) return;
        setDownloading(true);
        try {
            await exportNodeToPDF(docRef.current, `Proposal-${refLabel || "estimate"}.pdf`);
        } catch { /* ignore */ }
        finally { setDownloading(false); }
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="proposal-modal">
            <div className="w-full max-w-3xl my-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-display font-bold text-lg flex items-center gap-2">
                        <Sparkle size={18} weight="fill" className="text-[var(--rom-orange)]" /> Client Proposal
                    </h3>
                    <div className="flex items-center gap-2">
                        <button onClick={download} disabled={loading || downloading || !data} className="btn btn-primary" data-testid="proposal-download">
                            {downloading ? <CircleNotch size={14} className="animate-spin" /> : <FilePdf size={14} weight="bold" />} Download PDF
                        </button>
                        <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/15 text-white flex items-center justify-center hover:bg-white/25" data-testid="proposal-close"><X size={18} /></button>
                    </div>
                </div>

                {loading && (
                    <div className="bg-white rounded-[8px] p-16 text-center text-[var(--rom-text-2)]" data-testid="proposal-loading">
                        <CircleNotch size={28} className="animate-spin mx-auto mb-3 text-[var(--rom-orange)]" />
                        Drafting your client-ready proposal…
                    </div>
                )}
                {err && <div className="bg-white rounded-[8px] p-8 text-center text-[var(--rom-danger)]">{err}</div>}

                {data && (
                    <div ref={docRef} className="bg-white" style={{ width: "100%", color: "#1a2332" }} data-testid="proposal-doc">
                        {/* Header */}
                        <div style={{ background: "#1a2332", color: "#fff", padding: "28px 36px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                <div>
                                    <div style={{ fontSize: 12, letterSpacing: "0.18em", textTransform: "uppercase", color: "#e8682e", fontWeight: 700 }}>{FRANCHISE_NAME}</div>
                                    <div style={{ fontSize: 26, fontWeight: 800, marginTop: 4 }}>{data.title || `${lossLabel} Restoration Proposal`}</div>
                                </div>
                                <div style={{ textAlign: "right", fontSize: 12, color: "#c7cdd6" }}>
                                    <div style={{ fontWeight: 700, color: "#fff" }}>{APP_NAME}</div>
                                    <div>Ref {refLabel}</div>
                                    <div>{today}</div>
                                </div>
                            </div>
                        </div>

                        <div style={{ padding: "28px 36px" }}>
                            {/* Prepared for */}
                            <div style={{ display: "flex", gap: 32, fontSize: 13, marginBottom: 22, paddingBottom: 18, borderBottom: "1px solid #e6e9ee" }}>
                                <div>
                                    <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em", color: "#8a93a0", fontWeight: 700 }}>Prepared for</div>
                                    <div style={{ fontWeight: 600, marginTop: 3 }}>{inputs?.projectName || "Property Owner / Carrier"}</div>
                                    {inputs?.address && <div style={{ color: "#5a6472" }}>{inputs.address}</div>}
                                </div>
                                <div>
                                    <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em", color: "#8a93a0", fontWeight: 700 }}>Loss type</div>
                                    <div style={{ fontWeight: 600, marginTop: 3 }}>{lossLabel}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em", color: "#8a93a0", fontWeight: 700 }}>Affected area</div>
                                    <div style={{ fontWeight: 600, marginTop: 3 }}>{Number(inputs?.sqft || 0).toLocaleString()} sq ft</div>
                                </div>
                            </div>

                            {/* Intro */}
                            {data.intro && <p style={{ fontSize: 14, lineHeight: 1.7, marginBottom: 22 }}>{data.intro}</p>}

                            <Section title="Scope of Work" items={data.scope_of_work} />
                            <Section title="Our Approach" items={data.approach} />
                            <Section title="What's Included" items={data.inclusions} />

                            {/* Investment summary */}
                            <div style={{ marginTop: 26 }}>
                                <div style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: "0.12em", color: "#e8682e", fontWeight: 800, marginBottom: 10 }}>Investment Summary</div>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                                    <tbody>
                                        {buckets.map(([label, val]) => (
                                            <tr key={label} style={{ borderBottom: "1px solid #eef1f4" }}>
                                                <td style={{ padding: "9px 0", color: "#3a4456" }}>{label}</td>
                                                <td style={{ padding: "9px 0", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{formatCurrency(val)}</td>
                                            </tr>
                                        ))}
                                        <tr>
                                            <td style={{ padding: "14px 0 0", fontWeight: 800, fontSize: 16 }}>Total Estimate</td>
                                            <td style={{ padding: "14px 0 0", textAlign: "right", fontWeight: 800, fontSize: 18, color: "#1a2332", fontVariantNumeric: "tabular-nums" }}>{formatCurrency(s.grand_total)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                                <div style={{ marginTop: 8, fontSize: 11, color: "#8a93a0" }}>
                                    Rough Order of Magnitude · ±{s.confidence_pct}% confidence · projected {s.days} working days · approx. {s.crew_size}-person crew.
                                    {data.timeline_note ? ` ${data.timeline_note}` : ""}
                                </div>
                            </div>

                            {data.closing && <p style={{ fontSize: 14, lineHeight: 1.7, marginTop: 22 }}>{data.closing}</p>}

                            <div style={{ marginTop: 24, paddingTop: 16, borderTop: "1px solid #e6e9ee", fontSize: 10, color: "#9aa2ad", lineHeight: 1.6 }}>
                                This proposal is a directional Rough Order of Magnitude prepared by {FRANCHISE_NAME}. Final pricing is confirmed on
                                inspection and may adjust with discovered conditions. Powered by {APP_NAME}.
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function Section({ title, items }) {
    if (!items || items.length === 0) return null;
    return (
        <div style={{ marginTop: 22 }}>
            <div style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: "0.12em", color: "#e8682e", fontWeight: 800, marginBottom: 8 }}>{title}</div>
            <ul style={{ margin: 0, paddingLeft: 0, listStyle: "none" }}>
                {items.map((it, i) => (
                    <li key={i} style={{ display: "flex", gap: 8, fontSize: 13.5, lineHeight: 1.6, marginBottom: 6, color: "#2a3344" }}>
                        <CheckCircle size={16} weight="fill" color="#2f9e44" style={{ marginTop: 2, flexShrink: 0 }} />
                        <span>{it}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}
